import React, { useCallback, useEffect, useRef, useState } from 'react';
import { API_BASE as GLOBAL_API_BASE } from '../utils/api';
import MRTTableAdapter from './MRTTableAdapter';
import { createMRTColumnHelper } from 'material-react-table';
import { Box, Button, Chip, Typography, Stack } from '@mui/material';
import { Download, BarChart, Refresh, Visibility, VisibilityOff } from '@mui/icons-material';

type Submission = {
  id: number;
  submission_type: string;
  linked_record_id?: number | null;
  status: string;
  fda_response_file?: string | null;
  payload_snapshot?: string | null;
  generated_xml?: string | null;
  due_date?: string | null;
  created_by: number;
  created_at: string;
  updated_at: string;
};

type SubmissionLog = {
  id: number;
  submission_id: number;
  action: string;
  timestamp: string;
  user_id?: number | null;
  comments?: string | null;
};

type SubmissionFormData = {
  submission_type: string;
  linked_record_id?: number | null;
  payload_snapshot?: string | null;
};

const API_BASE = GLOBAL_API_BASE;

const SUBMISSION_TYPES = [
  { value: 'facility', label: 'Facility Registration', description: 'Facility registration and renewal submissions' },
  { value: 'product', label: 'Product Listing', description: 'Product ingredient and safety submissions' },
  { value: 'adverse_event', label: 'Adverse Event Report', description: 'Serious adverse event reports (15-day deadline)' },
  { value: 'label', label: 'Labeling Update', description: 'Product labeling and packaging updates' }
];

const STATUS_COLORS = {
  draft: 'bg-gray-100 text-gray-800',
  validated: 'bg-blue-100 text-blue-800',
  submitted: 'bg-green-100 text-green-800',
  acknowledged: 'bg-purple-100 text-purple-800',
  rejected: 'bg-red-100 text-red-800',
  in_review: 'bg-yellow-100 text-yellow-800'
};

const RegulatorySubmissions: React.FC = () => {
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [activeSubmission, setActiveSubmission] = useState<Submission | null>(null);
  const [logs, setLogs] = useState<SubmissionLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [formData, setFormData] = useState<SubmissionFormData>({
    submission_type: 'facility',
    linked_record_id: undefined,
    payload_snapshot: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [editingSubmissionId, setEditingSubmissionId] = useState<number | null>(null);
  const [filterType, setFilterType] = useState<string>('');
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  const [onlySAE, setOnlySAE] = useState<boolean>(false);
  const [labelTag, setLabelTag] = useState<string>('');
  const [overview, setOverview] = useState<{ due_this_month: number; overdue: number; upcoming_30d: number } | null>(null);
  const [facilities, setFacilities] = useState<Array<{ id: number; name: string }>>([]);
  const [products, setProducts] = useState<Array<{ id: number; name: string }>>([]);
  const [adverseEvents, setAdverseEvents] = useState<Array<{ id: number; description: string }>>([]);
  const [aggregateGroupBy, setAggregateGroupBy] = useState<'status'|'type'|'month'>('status');
  const [aggregateCounts, setAggregateCounts] = useState<Record<string, number>>({});
  const aggregateTotal = Object.values(aggregateCounts).reduce((a, b) => a + b, 0);
  // New: tabs for Simple / Aggregate / Auto Rules
  const [activeTab, setActiveTab] = useState<'simple'|'aggregate'|'auto'>('simple');
  const [preflight, setPreflight] = useState<any | null>(null);
  const listAbortRef = useRef<AbortController | null>(null);
  const skipFilterLoadRef = useRef<boolean>(false);
  // AE bulk-create modal
  const [showAEModal, setShowAEModal] = useState(false);
  const [aeSearch, setAeSearch] = useState('');
  const [aeSelected, setAeSelected] = useState<Record<number, boolean>>({});
  // Files refresh ticker to refetch files list after generation/attach actions
  const [filesRefreshTick, setFilesRefreshTick] = useState(0);

  // Note: no default date range; show full list by default

  const headers = useCallback(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('No authentication token found');
    }
    return { 
      'Authorization': `Bearer ${token}`, 
      'Content-Type': 'application/json',
      'Cache-Control': 'no-cache' 
    };
  }, []);

  // Build bulk filter payload from current UI filters
  const currentBulkPayload = useCallback(() => ({
    submission_type: filterType || undefined,
    status: filterStatus || undefined,
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
    is_serious: onlySAE || undefined,
    label_tag: labelTag || undefined
  }), [filterType, filterStatus, dateFrom, dateTo, onlySAE, labelTag]);

  // Guard against race conditions
  const requestSeqRef = useRef<number>(0);
  const loadSubmissions = useCallback(async () => {
    const localSeq = ++requestSeqRef.current;
    if (!loading) setLoading(true);
    setError(null);
    try {
      if (listAbortRef.current) { try { listAbortRef.current.abort(); } catch {} }
      const controller = new AbortController();
      listAbortRef.current = controller;
      const params = new URLSearchParams();
      params.append('skip', '0');
      params.append('limit', '1000');
      params.append('_', String(Date.now()));
      if (filterType) params.append('submission_type', filterType);
      if (filterStatus) params.append('status', filterStatus);
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      if (onlySAE && (!filterType || filterType === 'adverse_event')) params.append('is_serious', 'true');
      if (labelTag) params.append('label_tag', labelTag);
      const res = await fetch(`${API_BASE}/submissions/?${params}`, { headers: headers(), signal: controller.signal, cache: 'no-store' as RequestCache });
      if (!res.ok) {
        const errorText = await res.text();
        throw new Error(`Failed to load submissions: ${res.status} - ${errorText}`);
      }
      const data = await res.json();
      if (localSeq === requestSeqRef.current) setSubmissions(data);
    } catch (e: any) {
      if (e?.name === 'AbortError') {
        // ignore aborted requests
      } else {
        setError(e.message || 'Failed to load submissions');
        console.error('Load error:', e);
      }
    } finally {
      if (localSeq === requestSeqRef.current) setLoading(false);
    }
  }, [filterType, filterStatus, dateFrom, dateTo, onlySAE, labelTag, headers]);

  const loadSubmissionsAll = useCallback(async () => {
    const localSeq = ++requestSeqRef.current;
    if (!loading) setLoading(true);
    setError(null);
    try {
      if (listAbortRef.current) { try { listAbortRef.current.abort(); } catch {} }
      const controller = new AbortController();
      listAbortRef.current = controller;
      const params = new URLSearchParams();
      params.append('skip', '0');
      params.append('limit', '1000');
      params.append('_', String(Date.now()));
      const res = await fetch(`${API_BASE}/submissions/?${params}`, { headers: headers(), signal: controller.signal, cache: 'no-store' as RequestCache });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      if (localSeq === requestSeqRef.current) setSubmissions(data);
    } catch (e: any) {
      if (e?.name === 'AbortError') {
        // ignore
      } else {
        setError(e.message || 'Failed to load submissions');
      }
    } finally { if (localSeq === requestSeqRef.current) setLoading(false); }
  }, [headers]);

  const filteredAEs = adverseEvents.filter(a => {
    const q = aeSearch.trim().toLowerCase();
    if (!q) return true;
    return String(a.id).includes(q) || (a.description || '').toLowerCase().includes(q);
  });

  const toggleSelectAE = (id: number, checked: boolean) => {
    setAeSelected(prev => ({ ...prev, [id]: checked }));
  };

  const selectAllFilteredAEs = (checked: boolean) => {
    const update: Record<number, boolean> = { ...aeSelected };
    filteredAEs.forEach(a => { update[a.id] = checked; });
    setAeSelected(update);
  };

  const bulkCreateFromSelectedAEs = async () => {
    try {
      const ids = Object.entries(aeSelected).filter(([, v]) => !!v).map(([k]) => Number(k));
      if (ids.length === 0) { alert('Select at least one Adverse Event'); return; }
      const res = await fetch(`${API_BASE}/submissions/bulk/create`, {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify({ submission_type: 'adverse_event', record_ids: ids })
      });
      if (!res.ok) throw new Error(await res.text());
      await loadSubmissions();
      setShowAEModal(false);
      setAeSelected({});
      setAeSearch('');
      alert(`Created submissions for ${ids.length} adverse events`);
    } catch (e: any) {
      alert(e.message || 'Bulk create failed');
    }
  };

  const bulkGenerateSummaries = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/submissions/bulk/actions/generate-pdf-summary`, {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify(currentBulkPayload())
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      await loadSubmissions();
      alert(`Generated PDF summaries for ${data.processed} submissions`);
    } catch (e: any) {
      alert(`Bulk summary failed: ${e.message || e}`);
    }
  }, [currentBulkPayload, headers, loadSubmissions]);

  const bulkAttach3500A = useCallback(async () => {
    try {
      const payload = { ...currentBulkPayload(), submission_type: 'adverse_event' };
      const res = await fetch(`${API_BASE}/submissions/bulk/actions/attach-3500a`, {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify(payload)
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      await loadSubmissions();
      alert(`Attached FDA 3500A to ${data.processed} AE submissions`);
    } catch (e: any) {
      alert(`Bulk 3500A failed: ${e.message || e}`);
    }
  }, [currentBulkPayload, headers, loadSubmissions]);

  const bulkSubmit = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/submissions/bulk/actions/submit`, {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify(currentBulkPayload())
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      await loadSubmissions();
      alert(`Submitted ${data.submitted} submissions`);
    } catch (e: any) {
      alert(`Bulk submit failed: ${e.message || e}`);
    }
  }, [currentBulkPayload, headers, loadSubmissions]);

  const bulkGenerateLabelCompliance = useCallback(async () => {
    try {
      const payload = { ...currentBulkPayload(), submission_type: 'label' };
      const res = await fetch(`${API_BASE}/submissions/bulk/actions/generate-label-compliance`, {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify(payload)
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      await loadSubmissions();
      alert(`Generated Label Compliance PDFs for ${data.processed} submissions`);
    } catch (e: any) {
      alert(`Bulk label compliance failed: ${e.message || e}`);
    }
  }, [currentBulkPayload, headers, loadSubmissions]);

  const bulkGenerateSafetyDossierPro = useCallback(async () => {
    try {
      const payload = { ...currentBulkPayload(), submission_type: 'product' };
      const res = await fetch(`${API_BASE}/submissions/bulk/actions/generate-safety-dossier-pro`, {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify(payload)
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      await loadSubmissions();
      alert(`Generated Safety Dossier Pro PDFs for ${data.processed} submissions`);
    } catch (e: any) {
      alert(`Bulk Safety Dossier Pro failed: ${e.message || e}`);
    }
  }, [currentBulkPayload, headers, loadSubmissions]);

  

  const loadOverview = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/submissions/overview`, { headers: headers() });
      if (res.ok) setOverview(await res.json());
    } catch {}
  }, [headers]);

  const loadLinkedLists = useCallback(async () => {
    try {
      const [facRes, prodRes, aeRes] = await Promise.all([
        fetch(`${API_BASE}/facilities/`, { headers: headers() }),
        fetch(`${API_BASE}/products/`, { headers: headers() }),
        fetch(`${API_BASE}/adverse-events/`, { headers: headers() })
      ]);
      if (facRes.ok) setFacilities(await facRes.json());
      if (prodRes.ok) setProducts(await prodRes.json());
      if (aeRes.ok) setAdverseEvents(await aeRes.json());
    } catch (e) {
      console.error('Failed loading linked lists', e);
    }
  }, [headers]);

  const loadAggregate = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      params.append('group_by', aggregateGroupBy);
      if (filterType) params.append('submission_type', filterType);
      if (filterStatus) params.append('status', filterStatus);
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      if (onlySAE) params.append('is_serious', 'true');
      if (labelTag) params.append('label_tag', labelTag);
      const res = await fetch(`${API_BASE}/submissions/aggregate?${params.toString()}`, { headers: headers() });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setAggregateCounts(data.counts || {});
    } catch (e) {
      setAggregateCounts({});
    }
  }, [aggregateGroupBy, filterType, filterStatus, dateFrom, dateTo, onlySAE, labelTag, headers]);

  const exportAggregateCSV = () => {
    const rows = [['Group', 'Count'], ...Object.entries(aggregateCounts).map(([k, v]) => [k, String(v)])];
    const csv = rows.map(r => r.map(x => '"' + x.replace(/"/g, '""') + '"').join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `submissions_aggregate_${aggregateGroupBy}.csv`;
    document.body.appendChild(a);
    a.click();
    URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  const exportAggregateZIP = async () => {
    try {
      const params = new URLSearchParams();
      if (filterType) params.append('submission_type', filterType);
      if (filterStatus) params.append('status', filterStatus);
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      if (onlySAE) params.append('is_serious', 'true');
      if (labelTag) params.append('label_tag', labelTag);
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');
      const res = await fetch(`${API_BASE}/submissions/export.zip?${params.toString()}`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (!res.ok) throw new Error('Export failed');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'submissions_export.zip';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (e: any) {
      alert(e.message || 'Export failed');
    }
  };

  const downloadAggregatePDF = async () => {
    try {
      const params = new URLSearchParams();
      params.append('group_by', aggregateGroupBy);
      if (filterType) params.append('submission_type', filterType);
      if (filterStatus) params.append('status', filterStatus);
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      if (onlySAE) params.append('is_serious', 'true');
      if (labelTag) params.append('label_tag', labelTag);
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');
      const res = await fetch(`${API_BASE}/submissions/aggregate/report.pdf?${params.toString()}`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (!res.ok) throw new Error('Report generation failed');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `aggregate_report_${aggregateGroupBy}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (e: any) {
      alert(e.message || 'Report failed');
    }
  };

  useEffect(() => { 
    // Load only what the current tab needs
    if (activeTab === 'simple') {
      // Reset filters and avoid racing filtered loader
      skipFilterLoadRef.current = true;
      setActiveSubmission(null);
      setLogs([]);
      setPreflight(null);
      setSubmissions([]);
      setFilterType('');
      setFilterStatus('');
      setOnlySAE(false);
      setLabelTag('');
      setDateFrom('');
      setDateTo('');
      (async () => {
        try { await loadSubmissionsAll(); } finally { skipFilterLoadRef.current = false; }
      })();
      // Follow-up fetch to override any racey filter-based request
      setTimeout(() => { if (activeTab === 'simple') { loadSubmissionsAll(); } }, 250);
      loadOverview();
      loadLinkedLists();
    } else if (activeTab === 'aggregate') {
      loadAggregate();
      // Overview is nice-to-have but not required; skip for perf
    }
  }, [activeTab, loadSubmissionsAll, loadOverview, loadLinkedLists, loadAggregate]);

  // Auto-refresh submissions when filters change on Simple tab
  useEffect(() => {
    if (activeTab !== 'simple') return;
    if (skipFilterLoadRef.current) return;
    loadSubmissions();
  }, [filterType, filterStatus, dateFrom, dateTo, onlySAE, labelTag, loadSubmissions, activeTab]);

  // Refresh full list when window regains focus or tab becomes visible
  useEffect(() => {
    const onFocus = () => { if (activeTab === 'simple') loadSubmissionsAll(); };
    const onVisibility = () => { if (document.visibilityState === 'visible' && activeTab === 'simple') loadSubmissionsAll(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [activeTab, loadSubmissionsAll]);

  const exportZip = async () => {
    try {
      const params = new URLSearchParams();
      if (filterType) params.append('submission_type', filterType);
      if (filterStatus) params.append('status', filterStatus);
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      if (onlySAE) params.append('is_serious', 'true');
      if (labelTag) params.append('label_tag', labelTag);
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');
      const res = await fetch(`${API_BASE}/submissions/export.zip?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Export failed');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'submissions_export.zip';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (e: any) {
      alert(e.message || 'Export failed');
    }
  };

  const createSubmission = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    
    try {
      const body = {
        ...formData,
        status: 'draft',
        payload_snapshot: formData.payload_snapshot || null
      };
      
      const isEditing = editingSubmissionId !== null;
      const url = isEditing ? `${API_BASE}/submissions/${editingSubmissionId}` : `${API_BASE}/submissions/`;
      const method = isEditing ? 'PUT' : 'POST';
      const res = await fetch(url, { 
        method, 
        headers: headers(), 
        body: JSON.stringify(body) 
      });
      
      if (!res.ok) {
        const errorText = await res.text();
        throw new Error(`${isEditing ? 'Failed to update' : 'Failed to create'} submission: ${res.status} - ${errorText}`);
      }
      const saved = await res.json();
      // Ensure new/updated item is visible in list and selected
      setSubmissions(prev => {
        const rest = prev.filter(s => s.id !== saved.id);
        return [saved, ...rest];
      });
      if (filterType && filterType !== saved.submission_type) {
        setFilterType(saved.submission_type);
      }
      await loadSubmissionsAll();
      setShowCreateForm(false);
      setFormData({ submission_type: 'facility', linked_record_id: undefined, payload_snapshot: '' });
      setEditingSubmissionId(null);
      
      // Show success message
      alert(isEditing ? 'Submission updated successfully!' : 'Submission created successfully!');
      
    } catch (e: any) {
      setError(e.message || 'Failed to save submission');
      console.error('Create error:', e);
    } finally {
      setSubmitting(false);
    }
  };

  const selectSubmission = async (submission: Submission) => {
    setActiveSubmission(submission);
    try {
      const res = await fetch(`${API_BASE}/submissions/${submission.id}/logs`, { 
        headers: headers() 
      });
      
      if (res.ok) {
        const logsData = await res.json();
        setLogs(logsData);
      } else {
        console.error('Failed to load logs:', res.status);
        setLogs([]);
      }
    } catch (e) {
      console.error('Error loading logs:', e);
      setLogs([]);
    }
    // Load preflight status
    try {
      const resPf = await fetch(`${API_BASE}/submissions/${submission.id}/preflight`, { headers: headers() });
      if (resPf.ok) setPreflight(await resPf.json()); else setPreflight(null);
    } catch { setPreflight(null); }
  };

  const validateSubmission = async () => {
    if (!activeSubmission) return;
    
    try {
      const res = await fetch(`${API_BASE}/submissions/${activeSubmission.id}/validate`, { 
        method: 'POST', 
        headers: headers() 
      });
      
      if (res.ok) { 
        // Update local state immediately for UX
        setActiveSubmission(prev => prev ? { ...prev, status: 'validated' } : prev);
        // Refresh submissions list and preflight so Submit button enables without manual refresh
        await loadSubmissions();
        try {
          const pfRes = await fetch(`${API_BASE}/submissions/${activeSubmission.id}/preflight`, { headers: headers() });
          if (pfRes.ok) setPreflight(await pfRes.json());
        } catch {}
        alert('Submission validated successfully!');
      } else {
        const errorText = await res.text();
        alert(`Validation failed: ${res.status} - ${errorText}`);
      }
    } catch (e) {
      alert(`Validation error: ${e}`);
    }
  };

  const generateXML = async () => {
    if (!activeSubmission) return;
    
    try {
      const res = await fetch(`${API_BASE}/submissions/${activeSubmission.id}/generate-xml`, { 
        method: 'POST', 
        headers: headers() 
      });
      
      if (res.ok) { 
        await loadSubmissions();
        setFilesRefreshTick(t => t + 1);
        alert('XML generated successfully!');
      } else {
        const errorText = await res.text();
        alert(`XML generation failed: ${res.status} - ${errorText}`);
      }
    } catch (e) {
      alert(`XML generation error: ${e}`);
    }
  };

  const validateXMLStructure = async () => {
    if (!activeSubmission) return;
    try {
      const res = await fetch(`${API_BASE}/submissions/${activeSubmission.id}/validate-xml-structure`, { method: 'POST', headers: headers() });
      const data = await res.json();
      if (res.ok && data.valid) alert('XML structure is valid');
      else alert(`XML invalid: ${(data.errors || []).join('; ')}`);
    } catch (e) {
      alert(`Validation error: ${e}`);
    }
  };

  const generatePdfSummary = async () => {
    if (!activeSubmission) return;
    try {
      const res = await fetch(`${API_BASE}/submissions/${activeSubmission.id}/generate-pdf-summary`, { method: 'POST', headers: headers() });
      if (res.ok) { setFilesRefreshTick(t => t + 1); alert('Summary PDF generated. See Files list below to download.'); }
      else alert(`Failed: ${res.status}`);
    } catch (e) {
      alert(`Error: ${e}`);
    }
  };

  const attach3500A = async () => {
    if (!activeSubmission) return;
    try {
      const res = await fetch(`${API_BASE}/submissions/${activeSubmission.id}/attach-3500a`, { method: 'POST', headers: headers() });
      if (res.ok) { setFilesRefreshTick(t => t + 1); alert('FDA 3500A attached. See Files list to download.'); }
      else alert(`Failed: ${res.status}`);
    } catch (e) {
      alert(`Error: ${e}`);
    }
  };

  const generateFacilityRegistration = async () => {
    if (!activeSubmission) return;
    try {
      const res = await fetch(`${API_BASE}/submissions/${activeSubmission.id}/generate-facility-registration`, { method: 'POST', headers: headers() });
      const data = await res.json().catch(() => ({}));
      if (res.ok) { setFilesRefreshTick(t => t + 1); alert('Facility Registration PDF generated. See Files to download.'); }
      else alert(`Failed: ${res.status} ${data?.detail || ''}`);
    } catch (e) { alert(String(e)); }
  };

  const generateProductListing = async () => {
    if (!activeSubmission) return;
    try {
      const res = await fetch(`${API_BASE}/submissions/${activeSubmission.id}/generate-product-listing`, { method: 'POST', headers: headers() });
      const data = await res.json().catch(() => ({}));
      if (res.ok) { setFilesRefreshTick(t => t + 1); alert('Product Listing PDF generated. See Files to download.'); }
      else alert(`Failed: ${res.status} ${data?.detail || ''}`);
    } catch (e) { alert(String(e)); }
  };

  const generateSafetyDossierStd = async () => {
    if (!activeSubmission) return;
    try {
      const res = await fetch(`${API_BASE}/submissions/${activeSubmission.id}/generate-safety-dossier`, { method: 'POST', headers: headers() });
      const data = await res.json().catch(() => ({}));
      if (res.ok) { setFilesRefreshTick(t => t + 1); alert('Safety Dossier PDF generated. See Files to download.'); }
      else alert(`Failed: ${res.status} ${data?.detail || ''}`);
    } catch (e) { alert(String(e)); }
  };

  const generateSafetyDossierPro = async () => {
    if (!activeSubmission) return;
    try {
      const res = await fetch(`${API_BASE}/submissions/${activeSubmission.id}/generate-safety-dossier-pro`, { method: 'POST', headers: headers() });
      const data = await res.json().catch(() => ({}));
      if (res.ok) { setFilesRefreshTick(t => t + 1); alert('Safety Dossier Pro PDF generated. See Files to download.'); }
      else alert(`Failed: ${res.status} ${data?.detail || ''}`);
    } catch (e) { alert(String(e)); }
  };

  const generateLabelCompliance = async () => {
    if (!activeSubmission) return;
    try {
      const res = await fetch(`${API_BASE}/submissions/${activeSubmission.id}/generate-label-compliance`, { method: 'POST', headers: headers() });
      const data = await res.json().catch(() => ({}));
      if (res.ok) { setFilesRefreshTick(t => t + 1); alert('Label Compliance PDF generated. See Files to download.'); }
      else alert(`Failed: ${res.status} ${data?.detail || ''}`);
    } catch (e) { alert(String(e)); }
  };

  const downloadSubmissionFile = async (filename: string) => {
    if (!activeSubmission) return;
    try {
      const res = await fetch(`${API_BASE}/submissions/${activeSubmission.id}/files/${encodeURIComponent(filename)}`, { headers: headers() });
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (e) {
      alert(String(e));
    }
  };

  const getSubmissionFiles = async (): Promise<Array<{ filename: string; size: number }>> => {
    if (!activeSubmission) return [];
    try {
      const res = await fetch(`${API_BASE}/submissions/${activeSubmission.id}/files`, { headers: headers() });
      if (!res.ok) return [];
      const data = await res.json();
      return data.files || [];
    } catch { return []; }
  };

  const submitToFDA = async () => {
    if (!activeSubmission) return;
    
    if (activeSubmission.status !== 'validated') {
      alert('Submission must be validated before submitting to FDA');
      return;
    }
    
    try {
      const res = await fetch(`${API_BASE}/submissions/${activeSubmission.id}/submit`, { 
        method: 'POST', 
        headers: headers() 
      });
      
      if (res.ok) { 
        await loadSubmissions();
        alert('Submission sent to FDA successfully!');
      } else {
        const errorText = await res.text();
        alert(`FDA submission failed: ${res.status} - ${errorText}`);
      }
    } catch (e) {
      alert(`FDA submission error: ${e}`);
    }
  };

  const getStatusColor = (status: string) => {
    return STATUS_COLORS[status as keyof typeof STATUS_COLORS] || 'bg-gray-100 text-gray-800';
  };

  const filteredSubmissions = submissions.filter(submission => {
    if (filterType && submission.submission_type !== filterType) return false;
    if (filterStatus && submission.status !== filterStatus) return false;
    return true;
  });

  // Columns for MRT
  const columnHelper = createMRTColumnHelper<Submission>();
  const columns: any[] = [
    columnHelper.accessor('id', { header: 'ID', size: 80 }),
    columnHelper.accessor('submission_type', { header: 'Type', size: 180, filterVariant: 'select' }),
    columnHelper.accessor('linked_record_id', { header: 'Linked Record', size: 140 }),
    columnHelper.accessor('status', { header: 'Status', size: 120, filterVariant: 'select' }),
    columnHelper.accessor(row => (row.created_at ? new Date(row.created_at).toISOString().slice(0,10) : ''), { id: 'created_at', header: 'Created', size: 140, filterVariant: 'date-range' }),
    columnHelper.accessor(row => (row.due_date ? new Date(row.due_date).toISOString().slice(0,10) : ''), { id: 'due_date', header: 'Due Date', size: 140, filterVariant: 'date-range' }),
    columnHelper.display({
      id: 'actions',
      header: 'Actions',
      size: 180,
      Cell: ({ row }) => (
        <div className="flex justify-end space-x-2">
          <button
            onClick={(e) => { e.stopPropagation(); selectSubmission(row.original); }}
            className="px-2 py-1 text-sm rounded border border-gray-300 hover:bg-gray-50"
            title="View"
          >
            View
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setEditingSubmissionId(row.original.id);
              setFormData({
                submission_type: row.original.submission_type,
                linked_record_id: row.original.linked_record_id ?? undefined,
                payload_snapshot: row.original.payload_snapshot ?? ''
              });
              setShowCreateForm(true);
            }}
            className="px-2 py-1 text-sm rounded border border-indigo-300 text-indigo-700 hover:bg-indigo-50"
            title="Edit"
          >
            Edit
          </button>
        </div>
      ),
    }),
  ];

  return (
    <div className="p-6">
      {/* Module Explanation */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
        <h2 className="text-2xl font-bold text-blue-900 mb-4">📋 Regulatory Submissions & FDA Reporting</h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <h3 className="text-lg font-semibold text-blue-800 mb-3">What This Module Does</h3>
            <ul className="text-sm text-blue-700 space-y-2">
              <li>• <strong>Facility Registration</strong>: Submit facility information to FDA for MoCRA compliance</li>
              <li>• <strong>Product Listing</strong>: Submit product ingredients and safety data to FDA</li>
              <li>• <strong>Adverse Event Reports</strong>: Submit serious adverse events within 15-day deadline</li>
              <li>• <strong>Label Updates</strong>: Submit product labeling changes and safety updates</li>
              <li>• <strong>Track Status</strong>: Monitor submission progress and FDA responses</li>
              <li>• <strong>Generate Reports</strong>: Create FDA-compliant documents and summaries</li>
            </ul>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-blue-800 mb-3">How It Works</h3>
            <div className="text-sm text-blue-700 space-y-2">
              <div className="flex items-center gap-2">
                <span className="bg-blue-200 text-blue-800 px-2 py-1 rounded text-xs font-medium">1</span>
                <span>Create submission from existing record (facility, product, adverse event)</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="bg-blue-200 text-blue-800 px-2 py-1 rounded text-xs font-medium">2</span>
                <span>Validate data completeness and compliance</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="bg-blue-200 text-blue-800 px-2 py-1 rounded text-xs font-medium">3</span>
                <span>Generate FDA-compliant XML and documents</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="bg-blue-200 text-blue-800 px-2 py-1 rounded text-xs font-medium">4</span>
                <span>Submit to FDA and track acknowledgment</span>
              </div>
            </div>
          </div>
        </div>
        <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
          <p className="text-sm text-yellow-800">
            <strong>💡 Tip:</strong> Submissions are created from existing records in other modules. 
            First create your facilities, products, and adverse events, then create submissions to report them to FDA.
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-4 border-b border-gray-200">
        <nav className="flex -mb-px space-x-6" aria-label="Tabs">
          {[
            {key:'simple',label:'Simple Submissions'},
            {key:'aggregate',label:'Aggregate Reporting'},
            {key:'auto',label:'Auto Rules'}
          ].map(t => (
            <button key={t.key}
              onClick={() => { setActiveTab(t.key as any); setActiveSubmission(null); }}
              className={`whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm ${activeTab===t.key ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
            >{t.label}</button>
          ))}
        </nav>
      </div>

      {activeTab === 'simple' && (
        <>
          {/* Overview Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-600">Due This Month</div>
              <div className="text-2xl font-semibold text-gray-900 leading-tight">{overview?.due_this_month ?? '-'}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-600">Overdue</div>
              <div className="text-2xl font-semibold text-gray-900 leading-tight">{overview?.overdue ?? '-'}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-600">Due in 30 Days</div>
              <div className="text-2xl font-semibold text-gray-900 leading-tight">{overview?.upcoming_30d ?? '-'}</div>
            </div>
          </div>

          <div className="flex justify-between items-center mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Submission Management</h2>
              <p className="text-gray-600 mt-1">Create, validate, and submit regulatory documents to FDA</p>
            </div>
            <button 
              onClick={() => setShowCreateForm(true)}
              className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors"
            >
              + New Submission
            </button>
            <button
              onClick={() => setShowAEModal(true)}
              className="ml-3 border border-rose-300 text-rose-700 bg-white px-4 py-2 rounded-lg hover:bg-rose-50 transition-colors"
              title="Create submissions from existing Adverse Events"
            >
              From AEs…
            </button>
          </div>
        </>
      )}

      {activeTab === 'simple' && (
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex gap-4 items-center flex-wrap">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
            <select 
              value={filterType} 
              onChange={(e) => setFilterType(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm"
            >
              <option value="">All Types</option>
              {SUBMISSION_TYPES.map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select 
              value={filterStatus} 
              onChange={(e) => setFilterStatus(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm"
            >
              <option value="">All Statuses</option>
              <option value="draft">Draft</option>
              <option value="validated">Validated</option>
              <option value="submitted">Submitted</option>
              <option value="acknowledged">Acknowledged</option>
              <option value="rejected">Rejected</option>
              <option value="in_review">In Review</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Label Tag (for label submissions)</label>
            <input
              type="text"
              value={labelTag}
              onChange={(e) => setLabelTag(e.target.value)}
              placeholder="e.g., approved, draft"
              className="border border-gray-300 rounded-md px-3 py-2 text-sm"
            />
          </div>
          <div className="flex items-center gap-2 mt-6">
            <input id="saeOnly" type="checkbox" checked={onlySAE} onChange={(e) => setOnlySAE(e.target.checked)} />
            <label htmlFor="saeOnly" className="text-sm text-gray-700">SAE only (Adverse Events)</label>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">From</label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">To</label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm"
            />
          </div>
          <button 
            onClick={() => { setFilterType(''); setFilterStatus(''); setDateFrom(''); setDateTo(''); setOnlySAE(false); setLabelTag(''); loadSubmissionsAll(); }}
            className="mt-6 text-gray-500 hover:text-gray-700 text-sm"
          >
            Clear Filters
          </button>
          {/* Export and Bulk Operations removed from Simple tab to avoid duplication with Aggregate */}
        </div>
      </div>
      )}

      {/* Aggregate Reporting */}
      {activeTab === 'aggregate' && (
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Aggregate Reports</h3>
            <p className="text-sm text-gray-600">Comprehensive submission analytics and reporting</p>
            <p className="text-xs text-gray-500 mt-1">Tip: For very large datasets, start with a broad date range only (avoid product filters) and use background export.</p>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-700">Group by</label>
            <select value={aggregateGroupBy} onChange={(e) => setAggregateGroupBy(e.target.value as any)} className="border border-gray-300 rounded-md px-3 py-2 text-sm">
              <option value="status">Status</option>
              <option value="type">Type</option>
              <option value="month">Month</option>
            </select>
            <button onClick={loadAggregate} className="bg-indigo-600 text-white px-3 py-2 rounded-md text-sm hover:bg-indigo-700">Refresh</button>
            <button onClick={exportAggregateCSV} className="bg-gray-100 text-gray-800 px-3 py-2 rounded-md text-sm hover:bg-gray-200">📈 Download CSV</button>
            <button onClick={exportAggregateZIP} className="bg-slate-800 text-white px-3 py-2 rounded-md text-sm hover:bg-slate-900">📦 Export ZIP</button>
            <button onClick={downloadAggregatePDF} className="bg-red-600 text-white px-3 py-2 rounded-md text-sm hover:bg-red-700">📋 Download PDF Report</button>
            <button onClick={exportAggregateZIP} className="bg-amber-600 text-white px-3 py-2 rounded-md text-sm hover:bg-amber-700" title="Run export in background and download when ready">🕒 Background Export</button>
            <button onClick={bulkGenerateLabelCompliance} className="bg-teal-600 text-white px-3 py-2 rounded-md text-sm hover:bg-teal-700" title="Generate Label Compliance PDFs for filtered label submissions">🏷️ Label Compliance (Bulk)</button>
            <button onClick={bulkGenerateSafetyDossierPro} className="bg-emerald-600 text-white px-3 py-2 rounded-md text-sm hover:bg-emerald-700" title="Generate Safety Dossier Pro PDFs for filtered product submissions">🧪 Safety Dossier Pro (Bulk)</button>
          </div>
        </div>
        {Object.keys(aggregateCounts).length === 0 ? (
          <div className="text-gray-500 text-sm">No data for current filters</div>
        ) : (
          <div>
            <div className="flex items-center gap-2 mb-3">
              <span className="text-sm text-gray-600">Total</span>
              <span className="text-sm font-medium text-gray-900">{aggregateTotal}</span>
            </div>
            <div className="space-y-2">
              {Object.entries(aggregateCounts).map(([k, v]) => {
                const pct = aggregateTotal ? Math.round((v / aggregateTotal) * 100) : 0;
                return (
                  <div key={k}>
                    <div className="flex justify-between text-xs text-gray-600 mb-1">
                      <span>{k}</span>
                      <span>{v} ({pct}%)</span>
                    </div>
                    <div className="w-full bg-gray-100 rounded h-2">
                      <div className="bg-indigo-600 h-2 rounded" style={{ width: `${pct}%` }}></div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium">{error}</p>
            </div>
            <div className="ml-auto pl-3">
              <button 
                onClick={() => setError(null)}
                className="text-red-400 hover:text-red-600"
              >
                <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* AE Bulk Create Modal */}
      {activeTab === 'simple' && showAEModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-12 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-1">
              <div className="flex items-start justify-between mb-3">
                <h3 className="text-lg font-medium text-gray-900">Create Submissions from Adverse Events</h3>
                <button onClick={() => setShowAEModal(false)} className="text-gray-500 hover:text-gray-700">✕</button>
              </div>
              <div className="mb-3">
                <input
                  type="text"
                  value={aeSearch}
                  onChange={(e) => setAeSearch(e.target.value)}
                  placeholder="Search by ID or description"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                />
                <div className="mt-2 text-xs text-gray-500 flex items-center gap-2">
                  <span>Showing {filteredAEs.length} of {adverseEvents.length} events</span>
                  <button
                    type="button"
                    onClick={() => selectAllFilteredAEs(true)}
                    className="px-2 py-1 border rounded text-gray-700 hover:bg-gray-50"
                  >Select all filtered</button>
                  <button
                    type="button"
                    onClick={() => selectAllFilteredAEs(false)}
                    className="px-2 py-1 border rounded text-gray-700 hover:bg-gray-50"
                  >Clear selection</button>
                </div>
              </div>
              <div className="border rounded-md max-h-80 overflow-y-auto">
                {filteredAEs.length === 0 ? (
                  <div className="p-4 text-sm text-gray-500">No adverse events match your search.</div>
                ) : (
                  <ul className="divide-y">
                    {filteredAEs.map(a => (
                      <li key={a.id} className="p-3 flex items-start gap-3">
                        <input
                          type="checkbox"
                          checked={!!aeSelected[a.id]}
                          onChange={(e) => toggleSelectAE(a.id, e.target.checked)}
                          className="mt-1"
                        />
                        <div className="flex-1">
                          <div className="text-sm font-medium text-gray-900">AE #{a.id}</div>
                          <div className="text-xs text-gray-600">{a.description || '(no description)'}</div>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="flex justify-end gap-2 mt-4">
                <button
                  type="button"
                  onClick={() => setShowAEModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                >Cancel</button>
                <button
                  type="button"
                  onClick={bulkCreateFromSelectedAEs}
                  className="px-4 py-2 text-sm font-medium text-white bg-rose-600 rounded-md hover:bg-rose-700 disabled:opacity-50"
                  disabled={Object.values(aeSelected).filter(Boolean).length === 0}
                >Create Submissions</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      {activeTab === 'simple' && (loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Submissions List */}
          <div className="lg:col-span-2">
            <MRTTableAdapter<Submission> columns={columns} data={filteredSubmissions} />
          </div>

          {/* Submission Details */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Submission Details</h3>
              
              {activeSubmission ? (
                <div className="space-y-4">
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-sm font-medium text-gray-500">ID:</span>
                      <span className="text-sm text-gray-900">#{activeSubmission.id}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm font-medium text-gray-500">Type:</span>
                      <span className="text-sm text-gray-900">
                        {SUBMISSION_TYPES.find(t => t.value === activeSubmission.submission_type)?.label}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm font-medium text-gray-500">Status:</span>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(activeSubmission.status)}`}>
                        {activeSubmission.status.replace('_', ' ')}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm font-medium text-gray-500">Linked Record:</span>
                      <span className="text-sm text-gray-900">
                        {activeSubmission.linked_record_id ? (
                          <div>
                            <div className="font-medium">#{activeSubmission.linked_record_id}</div>
                            <div className="text-xs text-gray-500">
                              {activeSubmission.submission_type === 'facility' && 'Facility'}
                              {activeSubmission.submission_type === 'product' && 'Product'}
                              {activeSubmission.submission_type === 'adverse_event' && 'Adverse Event'}
                              {activeSubmission.submission_type === 'label' && 'Safety Document'}
                            </div>
                          </div>
                        ) : '-'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm font-medium text-gray-500">Created:</span>
                      <span className="text-sm text-gray-900">
                        {new Date(activeSubmission.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    {activeSubmission.due_date && (
                      <div className="flex justify-between">
                        <span className="text-sm font-medium text-gray-500">Due Date:</span>
                        <span className="text-sm text-gray-900">
                          <div className="font-medium">{new Date(activeSubmission.due_date).toLocaleDateString()}</div>
                          <div className={`text-xs ${new Date(activeSubmission.due_date) < new Date() ? 'text-red-500' : 'text-gray-500'}`}>
                            {new Date(activeSubmission.due_date) < new Date() ? 'Overdue' : 'Due'}
                          </div>
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Workflow Status */}
                  <div className="pt-4 border-t border-gray-200">
                    <h4 className="text-sm font-medium text-gray-900 mb-3">Workflow Status</h4>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <div className={`w-3 h-3 rounded-full ${activeSubmission.status === 'draft' ? 'bg-yellow-500' : 'bg-green-500'}`}></div>
                        <span className="text-sm text-gray-700">Draft Created</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className={`w-3 h-3 rounded-full ${['validated', 'submitted', 'acknowledged'].includes(activeSubmission.status) ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                        <span className="text-sm text-gray-700">Validated</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className={`w-3 h-3 rounded-full ${['submitted', 'acknowledged'].includes(activeSubmission.status) ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                        <span className="text-sm text-gray-700">Submitted to FDA</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className={`w-3 h-3 rounded-full ${activeSubmission.status === 'acknowledged' ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                        <span className="text-sm text-gray-700">FDA Acknowledged</span>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons with Preflight */}
                  <div className="space-y-2 pt-4 border-t border-gray-200">
                    <button 
                      onClick={validateSubmission}
                      disabled={!preflight || !preflight.can_validate}
                      title={!preflight || !preflight.can_validate ? 'Resolve blocking issues before validation' : ''}
                      className={`w-full px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                        preflight && preflight.can_validate
                          ? 'bg-yellow-500 text-white hover:bg-yellow-600'
                          : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      }`}
                    >
                      Validate
                    </button>
                    
                    <button 
                      onClick={generateXML}
                      disabled={!preflight || !preflight.can_generate}
                      title={!preflight || !preflight.can_generate ? 'Validation or required fields missing' : ''}
                      className={`w-full px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                        preflight && preflight.can_generate
                          ? 'bg-blue-500 text-white hover:bg-blue-600'
                          : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      }`}
                    >
                      Generate XML
                    </button>
                    
                    <button 
                      onClick={submitToFDA}
                      disabled={!preflight || !preflight.can_submit}
                      title={!preflight || !preflight.can_submit ? 'Submission requires validated status and no blocking issues' : ''}
                      className={`w-full px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                        preflight && preflight.can_submit
                          ? 'bg-green-600 text-white hover:bg-green-700'
                          : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      }`}
                    >
                      Submit to FDA
                    </button>
                    {preflight && (
                      <div className="text-sm mt-2">
                        <div className="text-gray-700">Due Date: <span className="font-medium">{preflight.due_date ? new Date(preflight.due_date).toLocaleDateString() : '-'}</span> {preflight.sla_days ? `(SLA: ${preflight.sla_days} days)` : ''}</div>
                        {preflight.issues && preflight.issues.length > 0 && (
                          <div className="text-red-700 mt-1">
                            <div className="font-semibold">Blocking Issues</div>
                            <ul className="list-disc ml-5">
                              {preflight.issues.map((x: string, idx: number) => (<li key={idx}>{x}</li>))}
                            </ul>
                          </div>
                        )}
                        {preflight.warnings && preflight.warnings.length > 0 && (
                          <div className="text-amber-700 mt-1">
                            <div className="font-semibold">Warnings</div>
                            <ul className="list-disc ml-5">
                              {preflight.warnings.map((x: string, idx: number) => (<li key={idx}>{x}</li>))}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Submission Logs */}
                  <div className="pt-4 border-t border-gray-200">
                    <h4 className="text-sm font-medium text-gray-900 mb-2">Activity Log</h4>
                    <div className="space-y-2 max-h-32 overflow-y-auto">
                      {logs.length > 0 ? (
                        logs.map(log => (
                          <div key={log.id} className="text-xs text-gray-600">
                            <div className="font-medium">{log.action}</div>
                            <div className="text-gray-500">
                              {new Date(log.timestamp).toLocaleString()}
                              {log.comments && ` - ${log.comments}`}
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="text-gray-500 text-xs">No activity logged</div>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-gray-500 text-center py-8">
                  Select a submission to view details
                </div>
              )}
            </div>
          </div>
        </div>
      ))}

      {/* Create Submission Modal */}
      {activeTab === 'simple' && showCreateForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Create New Submission</h3>
              
              <form onSubmit={createSubmission} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Submission Type *
                  </label>
                  <select 
                    value={formData.submission_type}
                    onChange={(e) => setFormData({...formData, submission_type: e.target.value, linked_record_id: undefined})}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                    required
                  >
                    {SUBMISSION_TYPES.map(type => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    {SUBMISSION_TYPES.find(t => t.value === formData.submission_type)?.description}
                  </p>
                </div>
                
                {formData.submission_type === 'facility' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Facility</label>
                    <select
                      value={formData.linked_record_id || ''}
                      onChange={(e) => setFormData({ ...formData, linked_record_id: e.target.value ? parseInt(e.target.value) : undefined })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                      required
                    >
                      <option value="">Select facility</option>
                      {facilities.map(f => (<option key={f.id} value={f.id}>{f.name}</option>))}
                    </select>
                  </div>
                )}
                {formData.submission_type === 'product' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Product</label>
                    <select
                      value={formData.linked_record_id || ''}
                      onChange={(e) => setFormData({ ...formData, linked_record_id: e.target.value ? parseInt(e.target.value) : undefined })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                      required
                    >
                      <option value="">Select product</option>
                      {products.map(p => (<option key={p.id} value={p.id}>{p.name}</option>))}
                    </select>
                  </div>
                )}
                {formData.submission_type === 'adverse_event' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Adverse Event</label>
                    <select
                      value={formData.linked_record_id || ''}
                      onChange={(e) => setFormData({ ...formData, linked_record_id: e.target.value ? parseInt(e.target.value) : undefined })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                      required
                    >
                      <option value="">Select event</option>
                      {adverseEvents.map(a => (<option key={a.id} value={a.id}>#{a.id} - {a.description?.slice(0,50)}</option>))}
                    </select>
                  </div>
                )}
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Payload Snapshot
                  </label>
                  <textarea
                    value={formData.payload_snapshot || ''}
                    onChange={(e) => setFormData({...formData, payload_snapshot: e.target.value})}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                    rows={3}
                    placeholder="Optional: JSON data snapshot (system can auto-generate later)"
                  />
                  <div className="text-xs text-gray-500 mt-1">
                    Tip: IDs are selected above from lists. You don't need to paste JSON unless you want to override.
                  </div>
                </div>
                
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowCreateForm(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50"
                  >
                    {submitting ? 'Creating...' : 'Create Submission'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'auto' && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Auto-Submission Rules (Coming Soon)</h3>
          <p className="text-sm text-gray-700 mb-3">Define automated submission rules for AE 15-day reporting, product listing cadence, facility renewals, and label approvals. Rules run on a schedule with validations and clear observability.</p>
          <ul className="list-disc pl-5 text-sm text-gray-700 space-y-1">
            <li>Serious AE auto-submission within 15 days</li>
            <li>Product listing periodic renewals</li>
            <li>Facility registration renewals</li>
            <li>Label approval based submissions</li>
          </ul>
          <div className="mt-4 text-xs text-gray-500">Note: This is a placeholder UI. Backend scaffolding and full rule editor will be added next.</div>
        </div>
      )}

      {activeSubmission && (
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 bg-white rounded-lg shadow p-6">
            <h4 className="text-md font-medium text-gray-900 mb-3">Advanced Actions</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <button onClick={validateXMLStructure} className="bg-yellow-500 text-white px-3 py-2 rounded">Validate XML Structure</button>
              <button onClick={generatePdfSummary} className="bg-gray-700 text-white px-3 py-2 rounded">Generate PDF Summary</button>
              {activeSubmission.submission_type === 'adverse_event' && (
                <button onClick={attach3500A} className="bg-rose-600 text-white px-3 py-2 rounded">Attach FDA 3500A</button>
              )}
              {activeSubmission.submission_type === 'facility' && (
                <button onClick={generateFacilityRegistration} className="bg-indigo-600 text-white px-3 py-2 rounded">Generate Facility Registration PDF</button>
              )}
              {activeSubmission.submission_type === 'product' && (
                <>
                  <button onClick={generateProductListing} className="bg-blue-600 text-white px-3 py-2 rounded">Generate Product Listing PDF</button>
                  <button onClick={generateSafetyDossierStd} className="bg-green-600 text-white px-3 py-2 rounded">Generate Safety Dossier (Std)</button>
                  <button onClick={generateSafetyDossierPro} className="bg-emerald-600 text-white px-3 py-2 rounded">Generate Safety Dossier Pro</button>
                </>
              )}
              {activeSubmission.submission_type === 'label' && (
                <button onClick={generateLabelCompliance} className="bg-teal-600 text-white px-3 py-2 rounded">Generate Label Compliance PDF</button>
              )}
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <h4 className="text-lg font-semibold text-gray-900 mb-3">📁 Generated Submission Files</h4>
            <p className="text-sm text-gray-600 mb-4">
              Download the generated regulatory documents for FDA submission:
            </p>
            <SubmissionFilesList fetchFiles={getSubmissionFiles} onDownload={downloadSubmissionFile} refreshKey={filesRefreshTick} />
          </div>
        </div>
      )}
    </div>
  );
};

const SubmissionFilesList: React.FC<{ fetchFiles: () => Promise<Array<{ filename: string; size: number }>>; onDownload: (filename: string) => void; refreshKey: number; }> = ({ fetchFiles, onDownload, refreshKey }) => {
  const [files, setFiles] = useState<Array<{ filename: string; size: number }>>([]);
  useEffect(() => { (async () => setFiles(await fetchFiles()))(); }, [fetchFiles, refreshKey]);
  
  const getFileDescription = (filename: string) => {
    if (filename.endsWith('.xml')) return 'SPL/ICSR XML submission file';
    if (filename.startsWith('form3500a_')) return 'FDA Form 3500A (MedWatch)';
    if (filename === 'summary.pdf') return 'Comprehensive submission summary';
    return 'Supporting document';
  };
  
  if (files.length === 0) return (
    <div className="text-center py-8">
      <div className="text-gray-500 text-sm mb-2">No files generated yet</div>
      <div className="text-xs text-gray-400">Use the actions above to generate XML, PDF summary, or FDA 3500A forms</div>
    </div>
  );
  
  return (
    <div className="space-y-3">
      {files.map(f => (
        <div key={f.filename} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
          <div className="flex-1">
            <div className="font-medium text-sm text-gray-900">{f.filename}</div>
            <div className="text-xs text-gray-500">{getFileDescription(f.filename)}</div>
            {f.size && <div className="text-xs text-gray-400">{(f.size / 1024).toFixed(1)} KB</div>}
          </div>
          <button 
            onClick={() => onDownload(f.filename)} 
            className="bg-indigo-600 text-white px-4 py-2 rounded-md text-sm hover:bg-indigo-700 transition-colors"
          >
            📥 Download
          </button>
        </div>
      ))}
    </div>
  );
};

export default RegulatorySubmissions;


